import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, TextField } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	ICreateUpdateAnnotationLabelModalProps,
	ICreateUpdateAnnotationLabelRequest,
} from 'src/features/annotation_label/domain/types';
import { CreateUpdateAnnotationLabelValidationSchema } from 'src/features/annotation_label/domain/validation';
import {
	ANNOTATION_LABEL_LOADING_KEY,
	createAnnotationLabel,
	updateAnnotationLabel,
} from 'src/features/annotation_label/store';
import { AppDispatch, RootState } from 'src/store';

const CreateUpdateAnnotationLabelModal: React.FC<
	ICreateUpdateAnnotationLabelModalProps
> = ({ isOpen, initialData, onClose, onSuccess }) => {
	const { siteSlug } = useParams();
	const dispatch = useDispatch<AppDispatch>();

	const isLoading = useSelector(
		(state: RootState) =>
			state.loading.loadingStates[ANNOTATION_LABEL_LOADING_KEY],
	);

	const {
		control,
		reset,
		handleSubmit,
		formState: { errors, isDirty },
	} = useForm<ICreateUpdateAnnotationLabelRequest>({
		resolver: yupResolver(CreateUpdateAnnotationLabelValidationSchema),
		defaultValues: {
			name: '',
		},
	});

	const handleSubmitClick = async (
		data: ICreateUpdateAnnotationLabelRequest,
	) => {
		if (!siteSlug) return;

		if (initialData) {
			dispatch(
				updateAnnotationLabel({
					annotationLabelId: initialData.id,
					request: data,
					siteSlug: siteSlug,
					onSuccess: () => {
						onSuccess();
						snackbar(
							'Annotation label updated successfully',
							'success',
						);
						reset({ name: '' });
					},
				}),
			);
		} else {
			dispatch(
				createAnnotationLabel({
					request: data,
					siteSlug: siteSlug,
					onSuccess: () => {
						onSuccess();
						snackbar(
							'Annotation label created successfully',
							'success',
						);
						reset({ name: '' });
					},
				}),
			);
		}
	};

	const handleDialogClose = () => {
		if (isLoading) return;
		reset();
		onClose();
	};

	useEffect(() => {
		reset({ name: initialData?.name || '' });
	}, [initialData]);

	if (!open) return null;

	return (
		<Dialog
			open={isOpen}
			PaperProps={{ sx: { width: 610, minHeight: 190 } }}
			onClose={handleDialogClose}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginTop: '8px',
					pb: 0,
				}}
			>
				<Typography
					sx={{
						fontSize: '20px',
						color: '#000000DE',
						fontWeight: 700,
					}}
				>
					{initialData ? 'Update' : 'Add'} Annotation Label
				</Typography>
				<IconButton onClick={handleDialogClose} sx={{ p: 0.5 }}>
					<CloseIcon
						fontSize='small'
						sx={{ color: '#000000DE', fontWeight: 900 }}
					/>
				</IconButton>
			</DialogTitle>

			<DialogContent>
				<form
					onSubmit={handleSubmit(handleSubmitClick)}
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '1rem',
						paddingTop: '1rem',
					}}
				>
					<Controller
						name='name'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Label Name'
								variant='outlined'
								fullWidth
								error={!!errors.name}
								helperText={errors.name?.message}
							/>
						)}
					/>

					<Box sx={{ display: 'flex', gap: 2 }}>
						<PrimaryButton
							type='submit'
							variant='contained'
							fullWidth
							disabled={!isDirty || isLoading}
							isLoading={isLoading}
							sx={{ height: '36px' }}
						>
							{initialData ? 'Update' : 'Create'}
						</PrimaryButton>

						<Button
							variant='outlined'
							color='error'
							fullWidth
							disabled={!isDirty || isLoading}
							onClick={() => {
								reset({ name: initialData?.name });
							}}
						>
							CLEAR
						</Button>
					</Box>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default CreateUpdateAnnotationLabelModal;
